import React, { createContext, useCallback, useEffect, useState } from 'react';
import type {
  AuthContextType,
  LoginRequest,
  User,
} from '@/types';
import { authService } from '@/services/auth.service';
import { getAccessToken, clearTokens } from '@/services/api';

const initialState: AuthContextType = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  isAdmin: false,
  login: async () => {},
  logout: () => {},
};

export const AuthContext = createContext<AuthContextType>(initialState);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;
  const isAdmin = user?.role === 'ADMIN';

  // Check for stored token on mount and fetch user
  useEffect(() => {
    const initAuth = async () => {
      const token = getAccessToken();

      if (token) {
        try {
          const userData = await authService.getMe();

          // Only allow ADMIN role access to dashboard
          if (userData.role !== 'ADMIN') {
            clearTokens();
            setUser(null);
          } else {
            setUser(userData);
          }
        } catch {
          // Token is invalid, clear it
          clearTokens();
          setUser(null);
        }
      }

      setIsLoading(false);
    };

    initAuth();
  }, []);

  // Listen for logout events from api interceptor
  useEffect(() => {
    const handleLogout = () => {
      setUser(null);
    };

    window.addEventListener('auth:logout', handleLogout);
    return () => {
      window.removeEventListener('auth:logout', handleLogout);
    };
  }, []);

  const login = useCallback(async (credentials: LoginRequest) => {
    setIsLoading(true);
    try {
      const response = await authService.login(credentials);

      // Only allow ADMIN role access to dashboard
      if (response.user.role !== 'ADMIN') {
        clearTokens();
        throw new Error('Access denied. Admin privileges required.');
      }

      setUser(response.user);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    authService.logout();
    setUser(null);
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    isAdmin,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
